import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Truck, History, Plane, CalendarClock, Trash2 } from "lucide-react";

export default function ShippingRates() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(null); // rate code
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ code: "", rate_per_cbm_rm: 340, sst_pct: 6.0, effective_from: new Date().toISOString().slice(0,10), notes: "" });

  const load = () => api.get("/shipping-rates").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const deleteRate = async (rate) => {
    const pw = window.prompt(`Delete rate ${rate.code} AND its full history?\nEnter admin password to confirm:`);
    if (!pw) return;
    try {
      await api.delete(`/shipping-rates/${rate.id}?password=${encodeURIComponent(pw)}`);
      toast.success(`Deleted ${rate.code}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const deleteHistory = async (h) => {
    const pw = window.prompt(`Delete this rate entry (effective ${h.effective_from})?\nEnter admin password to confirm:`);
    if (!pw) return;
    try {
      await api.delete(`/shipping-rates/history/${h.id}?password=${encodeURIComponent(pw)}`);
      toast.success("Rate entry deleted");
      // refresh
      const { data } = await api.get(`/shipping-rates/${historyOpen}/history`);
      setHistory(data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const openUpdate = (code, current) => {
    setForm({ code, rate_per_cbm_rm: current?.rate_per_cbm_rm || 340, sst_pct: current?.sst_pct ?? 6.0,
              effective_from: new Date().toISOString().slice(0,10), notes: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.effective_from) return toast.error("Effective date is required");
    try {
      await api.post(`/shipping-rates/${form.code}/history`, {
        code: form.code,
        rate_per_cbm_rm: parseFloat(form.rate_per_cbm_rm),
        sst_pct: parseFloat(form.sst_pct),
        effective_from: form.effective_from,
        notes: form.notes,
      });
      toast.success("New rate scheduled");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openHistory = async (code) => {
    setHistoryOpen(code);
    const { data } = await api.get(`/shipping-rates/${code}/history`);
    setHistory(data);
  };

  const isAir = (code) => code === "AIR-FRT";

  return (
    <div>
      <header className="mb-6">
        <div className="caption">Logistics</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Shipping Rates</h1>
        <p className="text-slate-500 mt-1 text-sm">Each rate keeps a full history. New rates apply only to shipments dated on/after their effective date. Air Freight is filled in per shipment once the amount is confirmed.</p>
      </header>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map(r => (
          <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-5" data-testid={`rate-card-${r.code}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="caption">{isAir(r.code) ? "Air freight" : "Sea freight"}</div>
                <div className="flex items-center gap-2 mt-1">
                  {isAir(r.code) ? <Plane className="w-4 h-4 text-sky-500" /> : <Truck className="w-4 h-4 text-slate-500" />}
                  <div className="font-mono font-bold text-lg">{r.code}</div>
                </div>
                <div className="text-sm text-slate-500">{r.name}</div>
              </div>
              <div className="text-right">
                {r.rate_per_cbm_rm > 0 ? (
                  <>
                    <div className="rm-badge text-lg font-bold">RM {r.rate_per_cbm_rm}</div>
                    <div className="text-xs text-slate-400">/ CBM + {r.sst_pct}% SST</div>
                    <div className="text-xs rm-badge">= RM {(r.rate_per_cbm_rm * (1 + r.sst_pct/100)).toFixed(2)} per CBM</div>
                  </>
                ) : (
                  <div className="text-xs text-amber-600 font-medium">Pending amount</div>
                )}
              </div>
            </div>
            {r.current_effective_from && (
              <div className="text-xs text-slate-500 font-mono">Effective from {r.current_effective_from}</div>
            )}
            {r.upcoming?.length > 0 && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                <div className="flex items-center gap-1 text-amber-700 font-medium mb-1">
                  <CalendarClock className="w-3 h-3" />Upcoming
                </div>
                {r.upcoming.map(u => (
                  <div key={u.id} className="font-mono">→ {u.effective_from}: RM {u.rate_per_cbm_rm}/CBM +{u.sst_pct}% SST</div>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={()=>openUpdate(r.code, r)} data-testid={`rate-update-${r.code}`}>
                <Plus className="w-4 h-4 mr-1" />Update rate
              </Button>
              <Button size="sm" variant="ghost" onClick={()=>openHistory(r.code)} data-testid={`rate-history-${r.code}`}>
                <History className="w-4 h-4 mr-1" />History
              </Button>
              <Button size="sm" variant="ghost" onClick={()=>deleteRate(r)} data-testid={`rate-delete-${r.code}`} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update rate · {form.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">The new rate applies only to shipments dated on/after the effective date. Older shipments keep their historical rate.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Rate (RM / CBM) *</Label><Input type="number" step="0.01" data-testid="rate-value-input"
                value={form.rate_per_cbm_rm}
                onChange={e=>setForm({...form, rate_per_cbm_rm:e.target.value})} /></div>
              <div><Label>SST %</Label><Input type="number" step="0.1" value={form.sst_pct}
                onChange={e=>setForm({...form, sst_pct:e.target.value})} /></div>
            </div>
            <div><Label>Effective from *</Label><Input type="date" data-testid="rate-effective-input"
              value={form.effective_from} onChange={e=>setForm({...form, effective_from:e.target.value})} /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes}
              onChange={e=>setForm({...form, notes:e.target.value})}
              placeholder="e.g. Courier updated freight charges" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="rate-save-button" className="bg-slate-900">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyOpen} onOpenChange={(o)=>!o && setHistoryOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Change log · {historyOpen}</DialogTitle></DialogHeader>
          <div className="rounded border border-slate-200 max-h-[60vh] overflow-y-auto">
            <table className="excel-table w-full text-xs">
              <thead><tr><th>Effective from</th><th>Rate</th><th>SST</th><th>Notes</th><th>By</th><th>At</th><th></th></tr></thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td className="font-mono">{h.effective_from}</td>
                    <td className="rm-badge">RM {h.rate_per_cbm_rm}</td>
                    <td className="font-mono">{h.sst_pct}%</td>
                    <td className="text-slate-500">{h.notes || "—"}</td>
                    <td className="font-mono text-slate-500">{h.created_by}</td>
                    <td className="font-mono text-slate-400">{h.created_at?.slice(0,16).replace("T"," ")}</td>
                    <td className="text-right">
                      <Button size="sm" variant="ghost" onClick={()=>deleteHistory(h)} data-testid={`rate-history-delete-${h.id}`} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400">No history yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
